import { InputType } from '@breeztech/breez-sdk-spark/web';
import { BulkPayment, BulkTypes } from '../models/BulkPayment';
import { Pleb, PlebData } from '../models/Pleb';
import { apiResponseToTransaction, PaymentMethod, PaymentType, Transaction, WalletType } from '../models/Transactions';
import { getCurrencyCode } from '../utils/Utils';
import { FakeData } from './FakeData';
import { Repository } from './Repository';
import { ConfirmSentRequestDto } from '../models/ConfirmSentRequestDto';
import { SearchInternalPaymentsRequestDto, SearchPaymentsRequestDto } from '../models/SearchPaymentsRequestDto';

const sanitizeBigInts = (value: unknown): any =>
  JSON.parse(
    JSON.stringify(value, (_key, v) =>
      typeof v === 'bigint' ? v.toString() : v
    )
  );

export interface LockedFunds {
  until: string;
  amountMsat: number;
}

export interface Wallet {
  balance: number;
  lnurl: string;
  lockedFunds?: LockedFunds;
  nwcConnectionString?: string;
  balanceFiat?: number;
  currencyCodeFiat?: string;
  shouldShowSeed?: boolean;
  staticAddress?: string;
  identityPubkey?: string;
}

export enum PaymentKind {
  PAY = 'pay',
  WITHDRAW = 'withdraw',
}

export interface ScanData {
  kind: PaymentKind;
  paymentRequest?: string;
  parseResponse?: InputType;
  domain?: string;
  callback?: string;
  maxSendable?: number;
  minSendable?: number;
  minWithdrawableMsat?: number;
  maxWithdrawableMsat?: number;
  description_hash?: string;
  description?: string;
  lnurl?: string;
  commentAllowed: number;
  opaFeesInPercent?: number;
  invoice?: string;
  fixedAmountMsat?: number;
  fixedComment?: string;
  lockedFunds?: LockedFunds;
  walletInternal?: {
    user: PlebData;
  };
}

export interface ScanResultSparkDto {
  paymentRequest: string;
  user: PlebData;
}

export enum PaymentRecipientType {
  singleRecipient = 'singleRecipient',
  bulkRecipient = 'bulkRecipient',
}

export interface SingleRecipientDto {
  name: string;
  imageUrl?: string;
  lnurl: string;
  slug?: string;
}

export interface BulkRecipientDto {
  type: BulkTypes;
  id: string;
  name: string;
  imageUrl?: string;
  recipientsCount?: number;
}

export interface PaymentRecipientDto {
  type: PaymentRecipientType;
  singleRecipient?: SingleRecipientDto;
  bulkRecipient?: BulkRecipientDto;
}

export interface LatestRecipientsResponseDto {
  latest: PaymentRecipientDto[];
  people: PaymentRecipientDto[];
  location: PaymentRecipientDto[];
  group: PaymentRecipientDto[];
  shouldShowWalletTip: boolean;
}

export enum TypeSearch {
  user = 'user',
  bulk_special = 'bulk_special',
  city = 'city',
  cityWithFilters = 'cityWithFilters',
  distanceFromMyself = 'distanceFromMyself',
  userAttribute = 'userAttribute',
}

export interface SearchResponse {
  type: TypeSearch;
  user?: Pleb;
  bulkSpecial?: BulkPayment;
  city?: City;
  distanceFromMyself?: DistanceFromMyself;
  imgUrl?: string;
  filters?: {
    badge?: string;
  }
}

export interface City {
  id: string;
  name: string;
  mainText: string;
  secondaryText: string;
  recipientsCount?: number;
  geoZap?: {
    addressType?: string;
    countryCode?: string;
    countryImgUrl?: string;
  }
}

export interface DistanceFromMyself {
  id: string;
  name: string;
}

export interface RecipientsFromTransaction {
  transactions: {
    recipient: Pleb;
  }[];
  count: number;
}

export interface PublicWalletSparkDto {
  id: string;
  identityPubkey: string;
  staticAddress: string;
  active: boolean;
  lnAddress: string | null;
}

export interface CreateBulkQuoteDto {
  network: 'lightning' | 'spark';
  type: BulkTypes;
  id: string;
  amountMsatPerRecipient: number;
  comment?: string;
  imageUrl?: string;
  membersOnly?: boolean;
  anonymous?: boolean;
  fromIdentityPubkey?: string;
  filters?: SearchFiltersDto;
}

export interface UpdateBulkQuoteDto {
  comment?: string;
  anonymous?: boolean;
  imageUrl?: string;
}

export type WalletServerInvoiceTypeValue = 'bolt11Invoice' | 'sparkInvoice';

export type WalletServerInvoiceWalletTypeValue = 'internal' | 'spark';

export interface WalletServerInvoiceBulkData {
  fromUserId: string;
  bulk: {
    id: string;
    type?: BulkTypes;
    name: string;
    imgUrl?: string;
    nbToSend: number;
    nbSendedSuccess: number;
    nbSendedError: number;
    geoZap?: {
      addressType?: string;
      countryCode?: string;
      countryImgUrl?: string;
    }
  },
  comment?: string;
  anonymous: boolean;
  imageUrl?: string;
}

export interface WalletServerInvoiceObject {
  id?: string;
  _id?: string;
  type: WalletServerInvoiceTypeValue;
  invoice: string;
  walletType?: WalletServerInvoiceWalletTypeValue;
  amountMsat: number;
  expiresAt: string | Date;
  paid: boolean;
  paidAt?: string | Date;
  bulkData?: WalletServerInvoiceBulkData;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

export interface BulkQuoteResponse {
  invoice: WalletServerInvoiceObject;
  fakePendingTransaction: Transaction;
}

export interface PaymentReceivedRequestDto {
  toIdentityPubkey?: string;
  paymentId: string;
  paymentHash: string;
  amount: number;
  fees: number;
  timestamp: number;
  comment?: string;
  btcTxId?: string;
  paymentMethod?: PaymentMethod | null;
}

export interface SearchFiltersDto {
  badge?: string;
}

export class WalletRepository extends Repository {
  async getWallet(): Promise<Wallet> {
    if(Repository.offlineMode)
    {
      return FakeData.getWallet();
    }
    
    const path = `/v1/wallet-internal?currencyCode=${getCurrencyCode()}`;
    const response = await Repository.client.get(path);
    return {
      balance: response.data.data.balance,
      lnurl: response.data.data.lnurl,
      lockedFunds: response.data.data?.lockedFunds ?? undefined,
      nwcConnectionString: response.data.data?.nwcConnectionString ?? undefined,
      balanceFiat: response.data.data?.balanceFiat ?? undefined,
      currencyCodeFiat: response.data.data?.currencyCodeFiat ?? undefined
    };
  }

  async getTransactions(limit: number = 10, page: number = 0): Promise<Transaction[]> {
    if(Repository.offlineMode)
    {
      return [FakeData.getTransaction()];
    }

    const path = `/v1/wallet-internal/payments?limit=${limit}&page=${page}&paymentTypes=${PaymentType.individual}&paymentTypes=${PaymentType.bulk}&paymentMethods=${PaymentMethod.LNURL}&paymentMethods=${PaymentMethod.INVOICE}&paymentMethods=${PaymentMethod.LNURL_WITHDRAW}&currencyCode=${getCurrencyCode()}`;
    const response = await Repository.client.get(path);
    return response.data.data.map((element: any) => {
      return apiResponseToTransaction(element);
    }) as Transaction[];
  }

  async getTransaction(paymentHash: string): Promise<Transaction> {
    if(Repository.offlineMode)
    {
      return FakeData.getTransaction();
    }

    const path = `/v1/wallet-internal/payment/${paymentHash}?currencyCode=${getCurrencyCode()}`;
    const response = await Repository.client.get(path);
    return apiResponseToTransaction(response.data.data);
  }

  async getRecipientsFromTransaction(paymentId: string, currentUserId: string, limit: number = 10, offset: number = 0): Promise<RecipientsFromTransaction> {
    const path = `/v1/wallet-internal/payment/${paymentId}/recipients?limit=${limit}&offset=${offset}`;
    const response = await Repository.client.get(path);
    return {
      transactions: (response.data.data.transactions as Array<any>)
          .map(member => ({recipient: Pleb.fromJson(member, currentUserId || '')}))
          .filter(member => member !== null && member.recipient !== null) as {recipient: Pleb}[],
      count: response.data.data.count
    }
  }

  async getLatestRecipients(limit: number = 20, page: number = 0): Promise<LatestRecipientsResponseDto> {
    const path = `/v2/wallet-internal/recipients/latest?limit=${limit}&page=${page}`;
    const response = await Repository.client.get(path);
    return response.data.data as LatestRecipientsResponseDto;
  }

  async markTransactionAsRead(paymentHash: string): Promise<void> {
    if(Repository.offlineMode)
    {
      return;
    }

    const path = `/v1/wallet-internal/payment/markAsRead`;
    await Repository.client.post(path, {
      paymentHash
    });
  } 

  async markTransactionAsAnnounced(paymentHash: string): Promise<void> {
    if(Repository.offlineMode)
    {
      return;
    }

    const path = `/v1/wallet-internal/payment/markAsAnnounced`;
    await Repository.client.post(path, {
      paymentHash
    });
  } 

  async scan(data: string): Promise<ScanData> {
    const path = 'v1/wallet-internal/scan';

    try {
      const response = await Repository.client.post(path, {
        data: data
      });
      return response.data.data as ScanData;
    } catch (err) {
      throw err;
    }
  }

  async payLnurl(data: string, amount: number, comment: string, userId?: string, anonymous?: boolean, imageUrl?: string): Promise<Transaction> {
    const path = 'v1/wallet-internal/pay';
    const response = await Repository.client.post(path, {
      data: data,
      amount: amount,
      comment: comment,
      userId: userId,
      anonymous: anonymous,
      imageUrl: imageUrl
    });
    return response.data.data as Transaction;
  }

  async regenerateSecretNWC(): Promise<string> {
    const path = 'v1/wallet-internal/nwc/regenerate-connection-string';
    const response = await Repository.client.post(path);
    return response.data.data.nwcConnectionString as string;
  }

  async scanBulkPayment(type: string, id: string, membersOnly?: boolean, walletType?: WalletType, filters?: SearchFiltersDto): Promise<BulkPayment> {
    const path = 'v1/wallet-internal/bulk/scan';
    const response = await Repository.client.post(path, {
      type: type,
      id: id,
      membersOnly: membersOnly,
      walletType: walletType ?? WalletType.INTERNAL,
      filters: filters ?? undefined
    });
    return response.data.data as BulkPayment;
  }

  async generateInvoice(amountMsat: number): Promise<{bolt11: string, paymentHash: string}> {
    const path = 'v1/wallet-internal/invoice/generate';
    const response = await Repository.client.post(path, {
      amountMsat: amountMsat,
    });
    return response.data.data;
  }

  async payBulkPayment(type: string, id: string, amount: number, comment: string, anonymous?: boolean, membersOnly?: boolean, imageUrl?: string, filters?: SearchFiltersDto): Promise<Transaction> {
    const path = 'v1/wallet-internal/bulk/pay';
    const response = await Repository.client.post(path, {
      type: type,
      id: id,
      amountMsat: amount * 1000,
      comment: comment,
      anonymous: anonymous,
      membersOnly: membersOnly,
      imageUrl: imageUrl,
      filters: filters ?? undefined
    });
    return response.data.data as Transaction;
  }

  async searchWallet(currentUserId: string, query: string, membersOnly?: boolean, signal?: AbortSignal): Promise<SearchResponse[]> {
    const path = `/v1/wallet-internal/users/search`;
    const response = await Repository.client.post(path, {
      searchTerm: query,
      searchTypes: [
        TypeSearch.user, 
        TypeSearch.bulk_special, 
        TypeSearch.city, 
        TypeSearch.distanceFromMyself, 
        TypeSearch.cityWithFilters,
        TypeSearch.userAttribute
      ],
      membersOnly: membersOnly
    }, { signal });
    return response.data.data.map((element: any) => {
      switch(element.type) {
        case TypeSearch.user:
          return {
            type: TypeSearch.user,
            user: Pleb.fromJson(element, currentUserId)
          }
        case TypeSearch.bulk_special:
          return {
            type: TypeSearch.bulk_special,
            bulkSpecial: {...element.bulkSpecial, type: BulkTypes.special} as BulkPayment,
            imgUrl: element?.imgUrl ?? undefined
          }
        case TypeSearch.userAttribute:
          return {
            type: TypeSearch.userAttribute,
            bulkSpecial: {...element.bulkSpecial, type: BulkTypes.userAttribute} as BulkPayment,
            imgUrl: element?.imgUrl ?? undefined
          }
        case TypeSearch.city:
          return {
            type: TypeSearch.city,
            city: element.city,
            filters: element.filters ?? undefined
          }
        case TypeSearch.distanceFromMyself:
          return {
            type: TypeSearch.distanceFromMyself,
            distanceFromMyself: element.distanceFromMyself
          }
      }
    }) as SearchResponse[];
  }

  async zapMessage(channelId: string, messageId: string, userId: string): Promise<Transaction> {
    const path = '/v1/wallet-internal/zap-message';
    const response = await Repository.client.post(path, {
      channelId: channelId,
      messageId: messageId,
      userId: userId,
      amountMsat: 1000
    });
    return apiResponseToTransaction(response.data.data);
  }

  async zapCall(callId: string, userId: string): Promise<Transaction> {
    const path = '/v1/wallet-internal/zap-call';
    const response = await Repository.client.post(path, {
      callId: callId,
      userId: userId,
      amountMsat: 1000
    });
    return apiResponseToTransaction(response.data.data);
  }

  async getActiveSparkWallet(): Promise<PublicWalletSparkDto | null> {
    const path = '/v1/wallet-spark/active';
    const response = await Repository.client.get(path);
    return response.data.data as PublicWalletSparkDto;
  }

  async registerSparkWallet(identityPubkey: string, staticAddress: string, balance: number): Promise<{wallet: PublicWalletSparkDto, showSaveSeedButton: boolean}> {
    const path = '/v1/wallet-spark/register';
    const response = await Repository.client.post(path, {
      identityPubkey,
      staticAddress,
      balance,
    });
    return response.data.data as {wallet: PublicWalletSparkDto, showSaveSeedButton: boolean};
  }

  async scanSparkWallet(paymentRequest: string): Promise<ScanResultSparkDto | null> {
    const path = '/v1/wallet-spark/scan';
    const response = await Repository.client.post(path, {
      paymentRequest,
    });
    return response.data.data as ScanResultSparkDto;
  }

  async getStatsSparkWallet(identityPubkey: string): Promise<{sent: number, received: number} | null> {
    const path = `/v1/wallet-spark/stats/${identityPubkey}`;
    const response = await Repository.client.get(path);
    return response.data.data as {sent: number, received: number};
  }

  async confirmSparkPayment(confirmSentDto: ConfirmSentRequestDto): Promise<Transaction> {
    const path = '/v1/wallet-spark/confirm-sent';
    const payload = {
      ...confirmSentDto,
    };

    const response = await Repository.client.post(path, payload);
    return apiResponseToTransaction(response.data.data);
  }

  async getSparkPayment(id: string): Promise<Transaction | null> {
    const path = `/v1/wallet-spark/payment/${id}`;
    const response = await Repository.client.get(path);
    if(response.data.data) {
      return apiResponseToTransaction(response.data.data);
    }
    return null;
  }

  async getSparkPayments(ids: string[]): Promise<Transaction[]> {
    const path = '/v1/wallet-spark/payments';
    const response = await Repository.client.post(path, { ids });
    return (response.data.data as any[]).map(apiResponseToTransaction);
  }

  async searchSparkPayments(data: SearchPaymentsRequestDto): Promise<Transaction[]> {
    const path = '/v1/wallet-spark/payments/search';
    const response = await Repository.client.post(path, data);
    return (response.data.data as any[]).map(apiResponseToTransaction);
  }

  async searchInternalPayments(data: SearchInternalPaymentsRequestDto): Promise<Transaction[]> {
    const path = '/v1/wallet-internal/payments/search';
    const response = await Repository.client.post(path, data);
    return (response.data.data as any[]).map(apiResponseToTransaction);
  }

  async markSparkPaymentAsRead(id: string): Promise<void> {
    const path = `/v1/wallet-spark/payment/${id}/markAsRead`;
    await Repository.client.post(path);
  }

  async markSparkPaymentAsAnnounced(id: string): Promise<void> {
    const path = `/v1/wallet-spark/payment/${id}/markAsAnnounced`;
    await Repository.client.post(path);
  }

  async markAllSparkPaymentsAsRead(): Promise<void> {
    const path = `/v1/wallet-spark/markAllPaymentsAsRead`;
    await Repository.client.post(path);
  }

  async markSeedAsSaved(identityPubkey: string): Promise<void> {
    const path = `/v1/wallet-spark/mark-seed-as-saved/${identityPubkey}`;
    await Repository.client.post(path);
  }

  async initSparkWallet(): Promise<void> {
    const path = `/v1/wallet-spark/init-spark-wallet`;
    await Repository.client.post(path);
  }

  async saveSparkLnAddress(identityPubkey: string, lnAddress: string): Promise<void> {
    const path = `/v1/wallet-spark/save-lnaddress/${identityPubkey}`;
    await Repository.client.post(path, { lnAddress });
  }

  async updateSparkBalance(identityPubkey: string, balance: number): Promise<void> {
    const path = `/v1/wallet-spark/balance/${identityPubkey}`;
    await Repository.client.post(path, { balance });
  }

  async askForBulkQuote(data: CreateBulkQuoteDto): Promise<BulkQuoteResponse> {
    const path = `/v1/wallet-server/bulk/quote`;
    const response = await Repository.client.post(path, data);
    return {
      invoice: response.data.data.invoice as WalletServerInvoiceObject,
      fakePendingTransaction: apiResponseToTransaction(response.data.data.fakePendingTransaction),
    }
  }

  async patchBulkQuote(id: string, data: UpdateBulkQuoteDto): Promise<BulkQuoteResponse> {
    const path = `/v1/wallet-server/bulk/quote/${id}`;
    const response = await Repository.client.patch(path, data);
    return {
      invoice: response.data.data.invoice as WalletServerInvoiceObject,
      fakePendingTransaction: apiResponseToTransaction(response.data.data.fakePendingTransaction),
    }
  }

  async uploadImage(picture: File): Promise<string> {
    const path = `v1/wallet-internal/upload-image`;
    
    const formData = new FormData();
    formData.append('picture', picture);

    try {
      const response = await Repository.client.post(path, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data.imageUrl as string;
    } catch (err) {
      throw err;
    }
  }

  async sparkPaymentReceived(paymentReceivedRequestDto: PaymentReceivedRequestDto): Promise<Transaction | null> {
    const path = '/v1/wallet-spark/payment-received';
    const response = await Repository.client.post(path, paymentReceivedRequestDto);
    if(response.data.data) {
      return apiResponseToTransaction(response.data.data);
    }
    return null;
  }
}

// Create a singleton instance
export const walletRepository = new WalletRepository();
